import * as vp from 'vopay';
import { Box, Title, Button, BackButton, Pagination, Table, Icon, Badge, Card } from 'vopay';
import { TableFiltersContainer } from 'UI';
import moment from 'moment';
import { FunctionComponent, useEffect, useState } from 'react';
import WebhooksHistorySearchForm from './WebhooksHistorySearchForm';
import useFetchPartnerWebhooks from '@/hooks/useApi/useFetchPartnerWebhooks';
import usePostResendWebhook from '@/hooks/useApi/usePostResendWebhook';
import styles from './WebhooksHistory.module.scss';
import { Toast } from 'vopay';
import { Trans, useTranslation } from 'react-i18next';
import { usePartnerData } from '@/providers/PartnerData.provider';

type FC = FunctionComponent;

type FilteredListType = vp.WebhooksList;

const WebhooksHistoryContainer: FC = () => {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [shouldShowEndDateError, setShouldShowEndDateError] = useState(false);
  const [eventType, setEventType] = useState('');
  const [webhooksResponse, getWebhooks] = useFetchPartnerWebhooks({});
  const [payloadIDList, setPayloadIDList] = useState({});
  const timeDifference = moment().subtract(1, 'month').format('YYYY-MM-DD');
  const currentDateTime = moment().format('YYYY-MM-DD');
  // bulk trigger webhooks feature (yet to implement)
  // const [allCheckbox, setAllCheckbox] = useState(false);
  const [initialLoad, setInitialLoad] = useState(false);
  const [postResendWebhookResponse, postResendWebhook] = usePostResendWebhook({});
  const [webhooksHistorySearchError, hasWebhooksHistorySearchError] = useState('');
  const [hasErrors, setHasErrors] = useState(false);
  const [showToast, shouldShowToast] = useState(false);
  const [successfullyRetriggered, setSuccessfullyRetriggered] = useState(false);
  const [webhooksRetriggered, setWebhooksRetriggered] = useState(false);
  const { t } = useTranslation('translations', { keyPrefix: 'Settings.Webhooks.History' });
  const {
    response: { isRoleViewOnly },
  } = usePartnerData();

  const transactionsPeriod = {
    FromDate: startDate || timeDifference,
    ToDate: endDate || currentDateTime,
  };
  const [payloadID, setPayloadID] = useState('');
  const [tempFilteredLists, setTempFilteredLists] = useState<FilteredListType[]>([]);
  const [selectedWebhooks, setSelectedWebhooks] = useState<FilteredListType[]>([]);

  // pagination
  const [currentPage, setCurrentPage] = useState(0);
  const PER_PAGE = 20;
  const pageCount = Math.ceil(Object.values(tempFilteredLists).length / PER_PAGE) || 0;

  useEffect(() => {
    if (webhooksResponse?.Success) {
      const webhookIDs: { [key: string]: string } = {
        All: 'All',
      };
      Object.values(webhooksResponse.WebhookNotifications).forEach((webhook) => {
        webhookIDs[webhook.WebhookID] = webhook.WebhookID;
      });

      setPayloadIDList(webhookIDs);
      setTempFilteredLists(
        Object.values(webhooksResponse.WebhookNotifications).map((webhook) => ({
          ...webhook,
        })),
      );

      setInitialLoad(true);
    }
  }, [webhooksResponse]);

  const handleTimeRangeChange = (timeRange: [vp.Timestamp, vp.Timestamp] | undefined) => {
    setStartDate(moment(timeRange?.[0]).format('YYYY-MM-DD'));
    setEndDate(moment(timeRange?.[1]).format('YYYY-MM-DD'));
  };

  const getWebhooksList = () => {
    const webhooksRequestParams: vp.WebhooksRequest = {
      StartDate: timeDifference,
      EndDate: currentDateTime,
      Type: eventType === 'All' ? '' : eventType,
    };

    const webhooksRequest = webhooksRequestParams;
    webhooksRequest.StartDate = transactionsPeriod.FromDate;
    webhooksRequest.EndDate = transactionsPeriod.ToDate;

    getWebhooks(webhooksRequest);
    setCurrentPage(0);
  };

  const handlePageClick = ({ selected: selectedPage }: { [idx: string]: number }) => {
    setCurrentPage(selectedPage);
  };

  // bulk trigger webhooks feature (yet to implement)

  // const onCheckedWebhook = (EventID: string) => {
  //   setTempFilteredLists(
  //     tempFilteredLists.map((filteredList) => {
  //       if (filteredList.EventID === EventID) {
  //         return { ...filteredList, isChecked: !filteredList.isChecked };
  //       }
  //       return filteredList;
  //     }),
  //   );
  // }

  // const checkToggleWebhooks = () => {
  //   setTempFilteredLists(
  //     tempFilteredLists.map((filteredList) => ({
  //       ...filteredList,
  //       isChecked: !allCheckbox,
  //     })),
  //   );
  //   setAllCheckbox(!allCheckbox);
  // };

  useEffect(() => {
    setTempFilteredLists(
      Object.values(webhooksResponse.WebhookNotifications || {}).filter(
        (data) =>
          (eventType === '' || eventType === 'All' || data.Type === eventType) &&
          (payloadID === '' || payloadID === 'All' || data.WebhookID === payloadID),
      ),
    );
  }, [eventType, payloadID, webhooksResponse]);

  useEffect(() => {
    setSelectedWebhooks(tempFilteredLists.filter((filteredLists) => filteredLists.isChecked));
  }, [tempFilteredLists]);

  const checkAllWebhooks = () => {
    setTempFilteredLists(
      tempFilteredLists.map((filteredList) => ({
        ...filteredList,
        isChecked: true,
      })),
    );
    // bulk trigger webhooks feature (yet to implement)
    // setAllCheckbox(true);
  };

  const unCheckAllWebhooks = () => {
    setTempFilteredLists(
      tempFilteredLists.map((filteredList) => ({
        ...filteredList,
        isChecked: false,
      })),
    );
    // bulk trigger webhooks feature (yet to implement)
    // setAllCheckbox(false);
  };

  function retriggerWebhooks(webhook: vp.RetriggerWebhookRequest) {
    const params: vp.RetriggerWebhookRequest = {
      EventID: webhook.EventID,
      Type: webhook.Type,
    };
    postResendWebhook(params);
    setWebhooksRetriggered(true);
  }

  useEffect(() => {
    if (webhooksRetriggered) {
      if (postResendWebhookResponse?.Success) {
        setSuccessfullyRetriggered(true);
      } else {
        setSuccessfullyRetriggered(false);
      }
      shouldShowToast(true);
    }
  }, [postResendWebhookResponse]);

  useEffect(() => {
    const ToDate = new Date(transactionsPeriod.ToDate);
    const FromDate = new Date(transactionsPeriod.FromDate);
    const dateDifference = Math.abs(ToDate.getTime() - FromDate.getTime());
    const daysDifference = Math.ceil(dateDifference / (1000 * 60 * 60 * 24));
    if (daysDifference > 31) {
      hasWebhooksHistorySearchError(t('searchError'));
      setHasErrors(true);
    } else {
      hasWebhooksHistorySearchError('');
      setHasErrors(false);
    }
  }, [transactionsPeriod]);

  useEffect(() => {
    if (showToast) {
      shouldShowToast(false);
    }
  }, [showToast]);

  const tableProps: vp.TableProps<vp.WebhooksList> = {
    id: 'webhooksHistoryTable',
    version: '2',
    columns: [
      {
        id: 'DateSent',
        header: t('Table.dateSent'),
        align: 'left',
        field: 'DateSent',
      },
      {
        id: 'Event',
        header: t('Table.event'),
        align: 'left',
        field: 'Type',
      },
      {
        id: 'Module',
        header: t('Table.module'),
        align: 'left',
        field: 'Module',
      },
      {
        id: 'PayloadID',
        header: t('Table.payloadID'),
        align: 'left',
        field: 'WebhookID',
      },
      {
        id: 'PayloadStatus',
        header: t('Table.payloadStatus'),
        align: 'left',
        template: ({ data }) => (
          <Box>
            <Badge label={vp.capitalizeFirstLetter(data.Status)} hasIcon color={vp.GetColorStatus(data.Status || '')} />
          </Box>
        ),
      },
      {
        id: 'Retrigger',
        header: '',
        align: 'right',
        template: ({ data }) => (
          <Box jc="fe">
            <Button
              id={`webhook-${data.WebhookID}`}
              label={t('Table.retriggerButton')}
              color="none-light"
              isDisabled={isRoleViewOnly}
              onClick={() => {
                retriggerWebhooks(data);
              }}
            />
          </Box>
        ),
      },
    ],
    rows: tempFilteredLists.map((item): { id: string; data: vp.WebhooksList } => ({
      id: String(item.WebhookID || ''),
      data: item,
    })),
  };

  return (
    <>
      <Box flx="col">
        <Box mb="md" jc="fs" ai="center" gap="common">
          <BackButton variant="arrow" backButtonEnabled />
          <Title title={t('pageTitle')} />
        </Box>
        <Card
          style={{
            padding: '24px 16px',
          }}
        >
          <Box flx="col" gap="sm">
            <TableFiltersContainer filtersApplied>
              <TableFiltersContainer.Trigger />
              <TableFiltersContainer.Content>
                <WebhooksHistorySearchForm
                  handleTimeRangeChange={handleTimeRangeChange}
                  shouldShowEndDateError={shouldShowEndDateError}
                  setShouldShowEndDateError={setShouldShowEndDateError}
                  handleTypeChange={setEventType}
                  eventType={eventType}
                  payloadIDList={payloadIDList}
                  getWebhooksList={() => {
                    getWebhooksList();
                  }}
                  handlePayloadIDChange={setPayloadID}
                  payloadID={payloadID}
                  webhooksHistorySearchError={webhooksHistorySearchError}
                  hasErrors={hasErrors}
                />
              </TableFiltersContainer.Content>
            </TableFiltersContainer>
          </Box>
          <Box mt="lg">
            <Table {...tableProps} />
          </Box>
          <Box flx="col" width="100%" position="relative">
            <div className={styles.selectedBar}>
              {selectedWebhooks.length > 0 && (
                <Box p="sm" jc="sb" ai="center">
                  <Box gap="none" ai="center">
                    <div>{t('selectedItemsLabel', { length: selectedWebhooks.length })}</div>
                    <Icon height="28px" width="25px" icon="line" />
                    <Button
                      height="28px"
                      color="yellow"
                      noPadding
                      noShadow
                      id="bulk-cancel"
                      isDisabled={isRoleViewOnly}
                      label={
                        <span className={styles.selectedBarSelectAll}>
                          {t('selectAllButton', { length: selectedWebhooks.length })}
                        </span>
                      }
                      onClick={() => {
                        checkAllWebhooks();
                      }}
                    />
                  </Box>
                  <Box gap="none">
                    <Button
                      height="28px"
                      color="secondary"
                      noShadow
                      id="bulk-collect"
                      isDisabled={isRoleViewOnly}
                      label={t('bulkRetriggerButton')}
                      onClick={() => {
                        // retriggerWebhooks();
                      }}
                    />
                    <Icon height="28px" width="25px" icon="line" />
                    <Button
                      height="28px"
                      color="yellow"
                      noPadding
                      noShadow
                      id="bulk-cancel"
                      isDisabled={isRoleViewOnly}
                      label={t('cancelButton')}
                      onClick={() => {
                        unCheckAllWebhooks();
                      }}
                    />
                  </Box>
                </Box>
              )}
            </div>
          </Box>
          <Box width="100%" jc="center" mt="md">
            {pageCount > 1 && (
              <Pagination pageCount={pageCount} forcePageNumber={currentPage} handlePageClick={handlePageClick} />
            )}
          </Box>
          {!initialLoad && (
            <Box flx="col" jc="center">
              <Box ai="center" jc="center">
                <Icon icon="search2" size="lg" />
              </Box>
              <Box ai="center" jc="center" pt="md">
                {t('searchInstruction')}
              </Box>
            </Box>
          )}
          {initialLoad && tempFilteredLists && Object.values(tempFilteredLists).length === 0 && (
            <Box flx="col" jc="center">
              <Box ai="center" jc="center" pt="md">
                {t('noRecords')}
              </Box>
            </Box>
          )}
        </Card>
      </Box>
      <Toast
        label={
          <>
            {successfullyRetriggered ? (
              <span>
                <b>{t('successToast')}</b>
              </span>
            ) : (
              <span>
                <Trans i18nKey="Settings.Webhooks.History.errorToast" components={{ b: <b /> }} />
              </span>
            )}
          </>
        }
        textColor={!successfullyRetriggered ? '#FBFBFB' : ''}
        backgroundColor={!successfullyRetriggered ? '#747474' : ''}
        isVisible={showToast}
      />
    </>
  );
};

export default WebhooksHistoryContainer;
